/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
*  Copyright 2019 Adobe
*  All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/


/* eslint-disable no-unused-vars */
import React, {Component} from 'react';
import ReactDOM from 'react-dom';
import Button from '@react/react-spectrum/Button';
import TextArea from '@react/react-spectrum/Textarea';
import {genericAnalyticEvent} from '../../utils/AnalyticsUtil';
/* eslint-enable no-unused-vars */
import './InlineFeedback.scss';

const isBrowser = typeof window !== `undefined` && typeof document !== `undefined`;

type Props = {
	intl: Object,
	feedbackContext: Object
};

type State = {
	feedbackStep: string,
	disableSubmitButton: boolean,
	feedbackValue1: string,
	feedbackValue2: string
};

class InlineFeedback extends Component<Props, State> {
	feedbackTextBox1: any;
	feedbackTextBox2: any;

	constructor() {
		super();
		this.state = {
			feedbackStep: 'step1',
			disableSubmitButton: true
		};

		this.postRenderTextBox1 = isBrowser;
		this.postRenderTextBox2 = isBrowser;
		this.answer = '';
	}

	handleAnalyticEvents(type, params) {
		let eventObj;
		let contentName;
		const {questionNumber, answer} = params;
		const {analyticStrings, intl} = this.props;
		switch (type) {
			case 'render':
				contentName = '';
				if (questionNumber === '1') {
					contentName = analyticStrings ? analyticStrings.title1 : intl.formatMessage({ id: 'inlineFeedback.title1' });
				}
				else {
					contentName = analyticStrings ? analyticStrings.title2 : intl.formatMessage({ id: 'inlineFeedback.title2' });
				}
				eventObj = {
					event: {
						subcategory: 'Feedback',
						type: 'render',
						subtype: 'question'
					},
					ui: {
						sort_order: questionNumber
					},
					content: {
						name: contentName
					}
				};
				break;
			case 'click':
				contentName = '';
				let contextGuid;
				if (questionNumber === '1') {
					contentName = analyticStrings ? analyticStrings.title1 : intl.formatMessage({ id: 'inlineFeedback.title1' });
					this.answer1 = answer;
				}
				else {
					contentName = analyticStrings ? analyticStrings.title2 : intl.formatMessage({ id: 'inlineFeedback.title2' });
				}
				if (questionNumber === '2') {
					contextGuid = this.answer1;
				}
				eventObj = {
					event: {
						subcategory: 'Feedback',
						type: 'click',
						subtype: 'question',
						value: answer,
						context_guid: contextGuid
					},
					ui: {
						sort_order: questionNumber
					},
					content: {
						name: contentName
					}
				};
				break;
		}
		const data = genericAnalyticEvent(eventObj);
		const {feedbackContext} = this.props;
		feedbackContext.analyticsCallbackHandler(data);
	}

	/*
	 * Checks if text is entered in the feedback text box
	 */
	checkTextBoxContent(text: string) {
		// Enable Submit button
		if (text && text.length > 0) {
			this.setState({disableSubmitButton: false});
		}
		// Disable Submit button
		else {
			this.setState({disableSubmitButton: true});
		}
	}

	/*
	 * Returns the first feedback text box
	 */
	showTextBox1() {

		// The render event gets called multiple times
		// as text is entered. We should only post the event once
		if (this.postRenderTextBox1) {
			this.handleAnalyticEvents('render', {questionNumber: '1'});
			this.postRenderTextBox1 = false;
		}

		return (<TextArea
			placeholder={this.props.intl.formatMessage({id: 'inlineFeedback.placeholder1'})}
			className='feedback-survey-text step1'
			ref={(component) => {this.feedbackTextBox1 = ReactDOM.findDOMNode(component);}}
			maxLength='255'
			onChange={e => this.checkTextBoxContent(e)}
		/>);
	}

	/*
	 * Returns the second feedback text box
	 */
	showTextBox2() {
		// The render event gets called multiple times
		// as text is entered. We should only post the event once
		if (this.postRenderTextBox2) {
			this.handleAnalyticEvents('render', {questionNumber: '2'});
			this.postRenderTextBox2 = false;

			// Select the textbox
			setTimeout(() => {
				if (this.feedbackTextBox2) {
					this.feedbackTextBox2.focus();
					this.feedbackTextBox2.select();
				}
			}, 700);
		}

		return (<TextArea
			className='feedback-survey-text step2'
			ref={(component) => {this.feedbackTextBox2 = ReactDOM.findDOMNode(component);}}
			maxLength='255'
			onChange={e => this.checkTextBoxContent(e)}
		/>);
	}

	/*
	 * Submits the entered feedback
	 */
	submitNext(answer: string) {
		this.setState({
			disableSubmitButton: true,
			feedbackStep: 'step2',
			feedbackTextBox1: answer
		},
		() => {
			if (!this.props.feedbackContext.isMobileOrTabletBrowser()) {
				window.scrollTo(0,document.body.scrollHeight);
				const {onFeedbackClick} = this.props.feedbackContext;
				onFeedbackClick && onFeedbackClick();
			}
			this.handleAnalyticEvents('click', {questionNumber: '1', answer});
		});
	}

	submitFeedback = () => {
		this.setState({
			feedbackTextBox2: this.feedbackTextBox2.value,
			feedbackStep: 'step3'},
		() => {
			if (!this.props.feedbackContext.isMobileOrTabletBrowser()) {
				window.scrollTo(0,document.body.scrollHeight);
				const {onFeedbackClick} = this.props.feedbackContext;
				onFeedbackClick && onFeedbackClick();
			}
			this.handleAnalyticEvents('click', {questionNumber: '2', answer: this.state.feedbackTextBox2});
		});

		/* istanbul ignore else */
		if (localStorage) {
			localStorage.setItem(`submittedFeedback-trailhead`, 'true');
		}
	}

	/*
	 * Shows the next button if text is entered in the first feedback text box
	 */
	showNextButton() {
		// The render event gets called multiple times
		// as text is entered. We should only post the event once
		if (this.postRenderTextBox1) {
			this.handleAnalyticEvents('render', {questionNumber: '1'});
			this.postRenderTextBox1 = false;
		}

		return (<div className='feedback-survey-button'>
			<Button
				label={this.props.intl.formatMessage({id: 'inlineFeedback.yes.button'})}
				onClick={() => this.submitNext('yes')}
			/>
			<Button
				label={this.props.intl.formatMessage({id: 'inlineFeedback.no.button'})}
				onClick={() => this.submitNext('no')}
			/>
		</div>);
	}

	/*
	 * Shows the submit button if text is entered in the second feedback text box
	 */
	showSubmitButton() {
		return (<div className='feedback-survey-button'>
			<Button
				label={this.props.intl.formatMessage({id: 'inlineFeedback.send.button'})}
				disabled={this.state.disableSubmitButton}
				onClick={this.submitFeedback}
			/>
		</div>);
	}

	renderThankYou() {
		return <div className='feedback-survey-title thanks'>
			<div className='feedback-survey-thanks-header'>
				<div className='feedback-survey-thanks'>
					{this.props.intl.formatMessage({ id: 'inlineFeedback.thanks' })}
				</div>
			</div>
		</div>;
	}

	/*
	 * Returns the feedback component
	 */
	renderFeedback() {
		let jsxArr = null;
		if (this.state.feedbackStep === 'step1') {
			jsxArr = <div className='feedback-survey-body step-1'>
				<div className='feedback-survey-subtitle'>
					{this.props.intl.formatMessage({ id: 'inlineFeedback.subtitle1' })}
				</div>
				<div className='feedback-survey-compact'>
					<div className='feedback-survey-title'>
						<span>
							{this.props.intl.formatMessage({ id: 'inlineFeedback.title1' })}
						</span>
						{this.showNextButton()}
					</div>
				</div>
			</div>;
		}
		else if (this.state.feedbackStep === 'step2') {
			jsxArr = <div className='feedback-survey-body'>
				<div className='feedback-survey-subtitle'>
					{this.props.intl.formatMessage({ id: 'inlineFeedback.subtitle2' })}
				</div>
				<div className='feedback-survey-title'>
					{this.props.intl.formatMessage({ id: 'inlineFeedback.title2' })}
				</div>
				<div className='feedback-survey-main'>
					{this.showTextBox2()}
					{this.showSubmitButton()}
				</div>
			</div>;
		}
		else if (this.state.feedbackStep === 'step3') {
			jsxArr = this.renderThankYou();
		}
		return jsxArr;
	}

	render() {
		if (this.props.intl.locale === 'en') {
			return (
				<div className='feedback-survey home-component'>
					{this.renderFeedback()}
				</div>
			);
		}
		return null;
	}
}


export default InlineFeedback;
